(() => {
  if (customElements.get('reefline-discovery')) return;

  class ReeflineDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.sectionId = this.dataset.sectionId;
      this.controller = null;
      this.submitTimer = null;
      this.facetsOpening = false;
      this.facetsOpenFrame = null;
      this.facetsTrapFrame = null;
      this.mobileQuery = window.matchMedia('(max-width: 960px)');
      this.onSubmit = this.onSubmit.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('popstate', this.onPopState);
      this.mobileQuery.addEventListener('change', this.onMediaChange);
      this.setupFacetsInterface();
    }

    disconnectedCallback() {
      this.controller?.abort();
      window.clearTimeout(this.submitTimer);
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('popstate', this.onPopState);
      this.mobileQuery.removeEventListener('change', this.onMediaChange);
      this.closeFacets({ restore: false });
      this.initialized = false;
    }

    setupFacetsInterface() {
      this.facetsToggle = this.querySelector('[data-facets-open]');
      this.facetsShell = this.querySelector('[data-facets-shell]');
      this.facetsPanel = this.querySelector('[data-facets-panel]');
      this.facetsTitle = this.facetsPanel?.querySelector('h2');
      this.syncFacetsMode();
    }

    onMediaChange() {
      this.closeFacets({ restore: false });
      this.syncFacetsMode();
    }

    syncFacetsMode() {
      if (!this.facetsShell || !this.facetsPanel) return;
      if (this.mobileQuery.matches) {
        this.facetsPanel.setAttribute('role', 'dialog');
        this.facetsPanel.setAttribute('aria-modal', 'true');
        if (this.facetsTitle?.id) this.facetsPanel.setAttribute('aria-labelledby', this.facetsTitle.id);
        if (!this.facetsShell.classList.contains('is-open')) {
          this.facetsShell.inert = true;
          this.facetsShell.setAttribute('aria-hidden', 'true');
        }
      } else {
        this.facetsShell.inert = false;
        this.facetsShell.removeAttribute('aria-hidden');
        this.facetsPanel.removeAttribute('role');
        this.facetsPanel.removeAttribute('aria-modal');
        this.facetsPanel.removeAttribute('aria-labelledby');
      }
    }

    openFacets() {
      if (!this.mobileQuery.matches || !this.facetsShell || !this.facetsPanel) return;
      if (this.facetsOpening || this.facetsShell.classList.contains('is-open')) return;
      this.facetsOpening = true;
      this.facetsShell.inert = false;
      this.facetsShell.removeAttribute('aria-hidden');
      this.facetsToggle?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('is-locked');
      this.facetsOpenFrame = window.requestAnimationFrame(() => {
        if (!this.isConnected || !this.facetsOpening || !this.facetsShell) return;
        this.facetsShell?.classList.add('is-open');
        this.facetsTrapFrame = window.requestAnimationFrame(() => {
          if (!this.isConnected || !this.facetsOpening || !this.facetsShell?.classList.contains('is-open')) return;
          this.facetsTrap = window.Reefline?.activateModal?.(this.facetsShell, {
            launcher: this.facetsToggle,
            initialFocus: this.facetsTitle
          });
        });
      });
    }

    closeFacets({ restore = true } = {}) {
      if (!this.facetsShell) return;
      this.facetsOpening = false;
      window.cancelAnimationFrame(this.facetsOpenFrame);
      window.cancelAnimationFrame(this.facetsTrapFrame);
      this.facetsOpenFrame = null;
      this.facetsTrapFrame = null;
      const wasOpen = this.facetsShell.classList.contains('is-open') || this.facetsToggle?.getAttribute('aria-expanded') === 'true';
      this.facetsShell.classList.remove('is-open');
      this.facetsToggle?.setAttribute('aria-expanded', 'false');
      window.Reefline?.deactivateModal?.(this.facetsShell, { restore });
      this.facetsTrap = null;
      if (this.mobileQuery.matches) {
        this.facetsShell.inert = true;
        this.facetsShell.setAttribute('aria-hidden', 'true');
      }
      if (wasOpen) document.body.classList.remove('is-locked');
    }

    onKeyDown(event) {
      if (!this.mobileQuery.matches || !this.facetsShell?.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeFacets();
        return;
      }
    }

    onSubmit(event) {
      const form = event.target.closest('[data-facet-form]');
      if (!form) return;
      event.preventDefault();
      window.clearTimeout(this.submitTimer);
      const fromSheet = this.mobileQuery.matches && this.facetsPanel?.contains(event.submitter || document.activeElement);
      this.render(this.urlForForm(form), {
        push: true,
        focusId: fromSheet ? this.facetsToggle?.id : document.activeElement?.id,
        closeSheet: fromSheet
      });
    }

    onChange(event) {
      const form = event.target.form?.matches('[data-facet-form]')
        ? event.target.form
        : event.target.closest('[data-facet-form]');
      if (!form || !event.target.matches('select, input[type="checkbox"], input[type="number"]')) return;
      this.updateDraftCount(form);
      if (this.mobileQuery.matches && this.facetsPanel?.contains(event.target)) return;
      window.clearTimeout(this.submitTimer);
      const delay = event.target.matches('input[type="number"]') ? 500 : 180;
      this.submitTimer = window.setTimeout(() => {
        this.render(this.urlForForm(form), { push: true, focusId: event.target.id });
      }, delay);
    }

    onClick(event) {
      const openButton = event.target.closest('[data-facets-open]');
      if (openButton && this.contains(openButton)) {
        event.preventDefault();
        this.openFacets();
        return;
      }

      const closeButton = event.target.closest('[data-facets-close]');
      if (closeButton && this.contains(closeButton)) {
        event.preventDefault();
        this.closeFacets();
        return;
      }

      const link = event.target.closest('[data-facet-link], [data-pagination] a');
      if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.origin !== window.location.origin) return;
      event.preventDefault();
      const fromSheet = this.mobileQuery.matches && this.facetsPanel?.contains(link);
      this.render(link.href, {
        push: true,
        focusId: fromSheet ? this.facetsToggle?.id : link.id,
        scroll: Boolean(link.closest('[data-pagination]')),
        closeSheet: fromSheet
      });
    }

    onPopState() {
      this.closeFacets({ restore: false });
      this.render(window.location.href, { push: false });
    }

    updateDraftCount(form) {
      let count = form.querySelectorAll('input[type="checkbox"]:checked').length;
      form.querySelectorAll('.facets__price').forEach((range) => {
        if (Array.from(range.querySelectorAll('input[type="number"]')).some((input) => input.value !== '')) count += 1;
      });
      this.querySelectorAll('[data-active-filter-count], [data-active-filter-count-label]').forEach((element) => {
        element.textContent = String(count);
        element.hidden = count === 0;
      });
    }

    urlForForm(form) {
      const url = new URL(form.action, window.location.origin);
      const parameters = new URLSearchParams(new FormData(form));
      parameters.forEach((value, key) => {
        if (value === '') parameters.delete(key);
      });
      url.search = parameters.toString();
      return url.toString();
    }

    async render(url, { push, focusId, scroll, closeSheet = false } = {}) {
      if (!this.sectionId) {
        window.location.assign(url);
        return;
      }

      this.controller?.abort();
      this.controller = new AbortController();
      const controller = this.controller;
      this.setAttribute('aria-busy', 'true');

      try {
        const requestUrl = new URL(url, window.location.origin);
        requestUrl.searchParams.set('section_id', this.sectionId);
        const response = await fetch(requestUrl, {
          signal: controller.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`Discovery request failed: ${response.status}`);

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const replacement = documentFragment.querySelector(`reefline-discovery[data-section-id="${CSS.escape(this.sectionId)}"]`);
        if (!replacement) throw new Error('Discovery section missing from response');

        if (closeSheet) this.closeFacets({ restore: false });
        this.innerHTML = replacement.innerHTML;
        this.setupFacetsInterface();
        if (push) window.history.pushState({}, '', url);

        const target = focusId ? this.querySelector(`#${CSS.escape(focusId)}`) : null;
        (target || this.querySelector('[data-results-count]'))?.focus({ preventScroll: true });
        if (scroll) this.querySelector('[data-results-container]')?.scrollIntoView({ block: 'start', behavior: 'auto' });
        const count = this.querySelector('[data-results-count]')?.textContent?.trim();
        window.Reefline?.announce?.(count);
        document.dispatchEvent(new CustomEvent('reefline:discovery:update', { detail: { sectionId: this.sectionId } }));
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(url);
      } finally {
        if (this.controller === controller) this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('reefline-discovery', ReeflineDiscovery);
})();
